import { markenKasten, markenLage, markenVersatz, resolveBrandColor } from '../brand.js';
import { chromaSchluesselFuerFfmpeg } from '../chroma.js';
import { STUFEN_JE_EINHEIT } from '../farbraeder.js';
import { geqAlpha } from '../maske.js';
import { Timeline, TRANSITIONS, sortedClips, timelineDuration, } from '../timeline.js';
import { LUMA } from '../messgeraete.js';
import { duckExpression, speechIntervals } from '../audio/duck.js';
import { gainExpression } from '../audio/pegel.js';
import { keyframeExpression, transformAt, movesOnCanvas } from '../animation/keyframes.js';
import { color, fmt, safeName, safePath } from './escape.js';
import { HDR_NACH_SDR } from '../hdr.js';
import { tonFilter } from '../toneffekte.js';
import { normalisierenFilter } from '../lautheit.js';
import { GraphBuilder, atempoChain } from './graph.js';
import { hatRampe, quellLaenge, quellZeit, rampeVon, setptsAusdruck, tonStufen } from '../rampe.js';
const EPS = 1e-4;
function addInput(ctx, args) {
    const index = ctx.inputs.length;
    ctx.inputs.push(...args);
    return index;
}
/** Count of `-i` occurrences so far == index of the next input stream. */
function inputIndex(ctx) {
    return ctx.inputs.filter((a) => a === '-i').length;
}
function addFileInput(ctx, path, pre = []) {
    const idx = inputIndex(ctx);
    addInput(ctx, [...pre, '-i', path]);
    return idx;
}
function addCachedFileInput(ctx, path) {
    const hit = ctx.fileInputs.get(path);
    if (hit !== undefined)
        return hit;
    const idx = addFileInput(ctx, path);
    ctx.fileInputs.set(path, idx);
    return idx;
}
function auxText(ctx, content) {
    const name = `text_${ctx.aux.length}.txt`;
    ctx.aux.push({ name, content });
    return `${ctx.o.workDir}/${name}`;
}
/** Der Radius der Ecken am Textkasten, in Punkten -- wie in der Vorschau (`textmaler.ts`). */
export function kastenRadius(schriftPx) {
    return 0.15 * schriftPx;
}
function drawText(ctx, spec) {
    const { style } = spec;
    const size = Math.max(8, Math.round(style.size * ctx.H));
    const raw = style.uppercase ? spec.text.toUpperCase() : spec.text;
    const file = auxText(ctx, raw);
    const px = Math.round(spec.x * ctx.W);
    const py = Math.round(spec.y * ctx.H);
    const xExpr = spec.mittig
        ? '(w-text_w)/2'
        : style.align === 'center' ? `${px}-text_w/2` : style.align === 'right' ? `${px}-text_w` : `${px}`;
    const opts = [
        `textfile=${safePath(file)}`,
        'reload=0',
        `fontsize=${size}`,
        `fontcolor=${spec.nurKasten ? 'black@0' : color(style.color)}`,
        `x=${xExpr}`,
        `y=${spec.mittig ? '(h-text_h)/2' : py}`,
        'line_spacing=8',
    ];
    const font = spec.fontFile ?? ctx.o.fontFile;
    if (font)
        opts.push(`fontfile=${safePath(font)}`);
    if (spec.nurKasten) {
        opts.push('box=1', 'boxcolor=white', `boxborderw=${Math.round(size * 0.35)}`);
        return `drawtext=${opts.join(':')}`;
    }
    if (style.boxColor && !spec.ohneKasten) {
        opts.push('box=1', `boxcolor=${color(style.boxColor, style.boxOpacity)}`, `boxborderw=${Math.round(size * 0.35)}`);
    }
    if (style.shadow) {
        opts.push('shadowcolor=0x000000@0.55', `shadowx=${Math.max(1, Math.round(size * 0.04))}`, `shadowy=${Math.max(1, Math.round(size * 0.04))}`);
    }
    // An outline is what makes white text readable over a bright sky, which a
    // shadow alone does not fix.
    if (style.outlineColor && style.outlineWidth > 0) {
        opts.push(`borderw=${Math.max(1, Math.round(size * style.outlineWidth))}`, `bordercolor=${color(style.outlineColor)}`);
    }
    return `drawtext=${opts.join(':')}`;
}
// ---------------------------------------------------------------------------
// effects
// ---------------------------------------------------------------------------
/**
 * Helligkeit, Kontrast und Sättigung -- **genau so, wie die Vorschau sie
 * rechnet** (7.131).
 *
 * Bis zum 21. September stand hier `eq=brightness=…:contrast=…:saturation=…`,
 * und das ist eine andere Rechnung als die der Vorschau:
 *
 *   - `eq` nimmt die Helligkeit **additiv und nach dem Kontrast**
 *     (`(in-0,5)·Kontrast + 0,5 + Helligkeit`), die Vorschau
 *     **multiplikativ und davor** (`(in·(1+Helligkeit) − 0,5)·Kontrast + 0,5`).
 *   - `eq` sättigt über die Farbdifferenzkanäle in YUV, die Vorschau über eine
 *     Helligkeitsmischung in sRGB.
 *
 * Gemessen an rgb(136, 68, 204) mit +0,2 / 1,4 / 0,6: der Film brachte
 * rgb(167, 129, 208), die Vorschau rgb(147, 78, 194) -- fünfzig Werte im
 * grünen Kanal, und das sieht man.
 *
 * **Wer wem folgt, ist keine Geschmacksfrage:** die Regler werden nach dem
 * gestellt, was man *sieht*. Also folgt der Film der Vorschau. Andersherum
 * wäre es auch nicht zu bauen -- die Sättigung von `eq` ist im Browser nicht
 * nachzurechnen, die der Vorschau im Film schon:
 *
 *   - `lutrgb` ist genau die Kennlinie, eine Tabelle je Kanal. Abgeschnitten
 *     wird **nach jedem Schritt**, wie in einer Kette von CSS-Filtern.
 *   - `colorchannelmixer` ist genau die Mischmatrix: auf der Diagonale
 *     `L + S·(1−L)`, daneben `L·(1−S)`. Bei S = 1 ist das die Einheitsmatrix,
 *     bei S = 0 steht in jeder Zeile die Helligkeit.
 *
 * Das Gamma steckt seit 7.133 in derselben Tabelle: je Kanal `pow(v, 1/g)`,
 * und genauso rechnen es jetzt beide Vorschauwege.
 */
/**
 * Was Helligkeit, Kontrast und Sättigung aus einer Farbe machen -- **die eine
 * Rechnung**, an der sich Vorschau und Film messen (7.131).
 *
 * Sie steht hier, damit es sie einmal gibt und nicht dreimal: das Stilblatt
 * rechnet sie als Kette von `filter()`-Funktionen, der Shader als drei
 * Schritte, der Film seit dem 21. September als `lutrgb` plus
 * `colorchannelmixer`. Abgeschnitten wird nach **jedem** Schritt -- so machen
 * es die CSS-Filter, und danach richten sich die anderen zwei (7.130).
 *
 * Eingang und Ausgang sind 0..255, wie überall dort, wo Farben gemessen
 * werden.
 */
export function farbwerteAuf(farbe, fx) {
    const klemme = (v) => Math.min(1, Math.max(0, v));
    const gamma = fx.gamma ?? 1;
    let c = farbe.map((v) => klemme((v / 255) * (1 + fx.brightness)));
    c = c.map((v) => klemme((v - 0.5) * fx.contrast + 0.5));
    // Das Gamma gehört zu den Tonwerten und steht deshalb bei ihnen -- nach dem
    // Kontrast, vor der Sättigung (7.133). Gerechnet wird es je Kanal
    // (`pow(v, 1/g)`), und nicht auf der Helligkeit allein, wie `eq` es tat:
    // eine Kennlinie je Kanal ist in allen drei Umsetzungen dieselbe Rechnung,
    // eine Helligkeitskurve wäre im Browser nicht nachzurechnen. Für die beiden
    // Vorlagen, die überhaupt ein Gamma setzen (1,06 und 0,94), liegen die
    // beiden Wege ein bis drei Werte auseinander -- am 21. September gemessen.
    if (gamma !== 1)
        c = c.map((v) => klemme(Math.pow(v, 1 / gamma)));
    const grau = LUMA[0] * c[0] + LUMA[1] * c[1] + LUMA[2] * c[2];
    c = c.map((v) => klemme(grau + fx.saturation * (v - grau)));
    return c.map((v) => Math.round(v * 255));
}
export function farbwerteKette(fx) {
    const schritte = [];
    const hell = 1 + fx.brightness;
    if (hell !== 1 || fx.contrast !== 1 || fx.gamma !== 1) {
        // `val` läuft von 0 bis 255; die Mitte des Kontrasts liegt bei 127,5,
        // weil sie in der Vorschau bei 0,5 liegt.
        let kurve = `clip((clip(val*${fmt(hell)},0,255)-127.5)*${fmt(fx.contrast)}+127.5,0,255)`;
        // Und das Gamma in derselben Tabelle: eine Kennlinie, ein Durchgang
        // (7.133). Vorher stand dafür `eq=gamma=`, das nur die Helligkeit
        // anhebt -- eine Rechnung, die die Vorschau nicht nachmachen kann.
        if (fx.gamma !== 1)
            kurve = `clip(255*pow(${kurve}/255,${fmt(1 / fx.gamma)}),0,255)`;
        schritte.push(`lutrgb=r='${kurve}':g='${kurve}':b='${kurve}'`);
    }
    if (fx.saturation !== 1) {
        const s = fx.saturation;
        const kanaele = ['r', 'g', 'b'];
        const teile = [];
        for (let i = 0; i < 3; i++) {
            for (let j = 0; j < 3; j++) {
                const l = LUMA[j];
                teile.push(`${kanaele[i]}${kanaele[j]}=${fmt(i === j ? l + s * (1 - l) : l * (1 - s))}`);
            }
        }
        schritte.push(`colorchannelmixer=${teile.join(':')}`);
    }
    return schritte;
}
function applyEffects(ctx, label, clip) {
    let cur = label;
    for (const fx of clip.effects) {
        switch (fx.type) {
            case 'colorAdjust':
                cur = ctx.g.pipe(cur, farbwerteKette(fx));
                break;
            case 'blur':
                cur = ctx.g.pipe(cur, [`gblur=sigma=${fmt(fx.amount)}`]);
                break;
            case 'sharpen':
                cur = ctx.g.pipe(cur, [`unsharp=5:5:${fmt(fx.amount)}:5:5:0`]);
                break;
            case 'vignette': {
                // **In RGB rechnen, und den Alphakanal aufheben** (7.140).
                //
                // Gemessen am 22. September an einer Fläche rgb(187,221,255) mit
                // Stärke 0,6: in der Ecke bringt `vignette` auf einem YUV-Strom
                // rgb(50,64,74), die Vorschau rechnet rgb(66,78,90) -- sechzehn Werte.
                // Der Filter dämpft dort die *Helligkeit*, die Vorschau dämpft die
                // drei Kanäle. Dieselbe Sorte wie 7.131 und 7.133. Mit `format=gbrp`
                // davor stimmen beide auf einen Wert genau.
                //
                // Und er lässt den Alphakanal nicht stehen: eine halb durchsichtige
                // Fläche kam mit 255 heraus, gleich in welchem Format. Also wird das
                // Alpha danebengelegt und danach wieder angehängt -- so wie es die
                // Vorschau tut, die nur die Farbe dämpft.
                const [fuerFarbe, fuerAlpha] = ctx.g.split(cur, 2);
                const gedaempft = ctx.g.pipe(fuerFarbe, [
                    'format=gbrp',
                    `vignette=angle=${fmt((Math.PI / 5) * (0.5 + fx.amount))}`,
                    'format=rgba',
                ]);
                const alpha = ctx.g.pipe(fuerAlpha, ['format=rgba', 'alphaextract']);
                const zusammen = ctx.g.label('v');
                ctx.g.chain([gedaempft, alpha], ['alphamerge'], [zusammen]);
                cur = zusammen;
                break;
            }
            case 'farbraeder': {
                // Farbräder (S14). Steht alles auf null, kommt kein Filter in die
                // Kette: ein Filter, der nichts tut, kostet trotzdem Zeit.
                const [rs, gs, bs] = fx.tiefen;
                const [rm, gm, bm] = fx.mitten;
                const [rh, gh, bh] = fx.hoehen;
                if ([rs, gs, bs, rm, gm, bm, rh, gh, bh].every((z) => Math.abs(z) < 1e-6))
                    break;
                // **Hier stand `colorbalance=...:pl=1`, und das war etwas anderes als
                // die Vorschau** (7.146). Gemessen am 22. September an rgb(136,68,204)
                // mit „Mitten wärmer“: der Film rgb(130,67,202), die Vorschau
                // rgb(225,68,166) -- 95 von 255 auseinander, auf zwei Dritteln der
                // Ebene.
                //
                // Zwei Unterschiede stecken darin. `colorbalance` gewichtet seine drei
                // Bänder nach der *Helligkeit des Punktes* (also über alle drei Kanäle
                // hinweg), die Vorschau nach dem Wert **des Kanals**; und `pl=1` holt
                // die Helligkeit danach wieder zurück, was die Vorschau nicht tut.
                //
                // Die Richtung ist die von 7.131 und 7.134: der Film folgt der
                // Vorschau. Und hier ist sie nicht einmal wählbar -- der alte
                // Vorschauweg ist eine Kennlinie je Kanal
                // (`feComponentTransfer type="table"`), und eine Gewichtung nach der
                // Helligkeit des ganzen Punktes lässt sich darin gar nicht
                // hinschreiben. Also rechnet der Export jetzt dieselbe Kennlinie:
                // `farbrampe`, als Ausdruck über `val`.
                // In Anführungszeichen, weil im Ausdruck Kommas stehen -- ohne sie
                // trennten die den Filter und nicht seine Klammern.
                const kurve = (tief, mitte, hoch) => `'clip(val+(max(0,1-2*val/255)*${fmt(tief)}+(1-abs(2*val/255-1))*${fmt(mitte)}` +
                    `+max(0,2*val/255-1)*${fmt(hoch)})*${fmt(STUFEN_JE_EINHEIT)},0,255)'`;
                // Auf Ebenen mit Alphakanal, und nicht auf dem Strom, wie er gerade
                // daliegt: auf yuva420p rechnete `lut` die Kennlinie auf Y, U und V
                // -- dieselbe Falle wie bei der Vignette (7.140) und dem Look (7.141).
                cur = ctx.g.pipe(cur, [
                    'format=gbrap',
                    `lut=r=${kurve(rs, rm, rh)}:g=${kurve(gs, gm, gh)}:b=${kurve(bs, bm, bh)}`,
                    'format=rgba',
                ]);
                break;
            }
            case 'stabi':
                // Sitzt nicht hier, sondern direkt hinter dem Einpassen: stabilisiert
                // wird das Kamerabild, bevor Tempo und Farbe daran rühren -- und der
                // erste Durchgang muss dieselben Bilder sehen wie der zweite.
                break;
            case 'chroma':
                // Greenscreen (S11). `chromakey` rechnet in YUV und schreibt in den
                // Alphakanal -- ohne `yuva420p` davor hätte es keinen, und die Farbe
                // bliebe stehen. Sichtbar wird das Loch erst auf einer oberen Spur:
                // unter der Basisspur liegt nichts, was durchscheinen könnte.
                //
                // **Der Schlüssel kommt fertig umgerechnet herein** (7.145). Aus
                // `#rrggbb` macht `chromakey` sonst selbst ein Farbpaar -- mit der
                // Matrix für vollen Umfang, während das Bild daneben mit der für
                // begrenzten dasteht. Gemessen am 22. September: ein Bild in
                // 0x00b140 gegen den Schlüssel 0x00b140 ergibt einen Abstand von
                // 0,027 statt null, und die Farbe bleibt unter einer Toleranz von
                // 0,028 einfach stehen. Mit `yuv=1` vergleicht der Filter, was das
                // Bild trägt, und die Vorschau rechnet dieselbe Zahl.
                cur = ctx.g.pipe(cur, [
                    'format=yuva420p',
                    `chromakey=color=${chromaSchluesselFuerFfmpeg(fx.color)}:yuv=1` +
                        `:similarity=${fmt(fx.toleranz)}:blend=${fmt(fx.weich)}`,
                ]);
                break;
            case 'lut': {
                if (!ctx.o.lutDir) {
                    ctx.warnings.push(`LUT "${fx.name}" skipped: no LUT directory configured.`);
                    break;
                }
                const path = `${ctx.o.lutDir}/${safeName(fx.name)}.cube`;
                // **Auf Ebenen und nicht auf gepackten Punkten** (7.141).
                //
                // `lut3d` selbst rechnet richtig -- an einer *genauen* RGB-Quelle
                // trifft es den gerechneten Wert auf einen Wert genau (gemessen am
                // 22. September: rgb(136,68,204) mit „teal-orange“ ergibt
                // rgb(138,58,225), gerechnet rgb(139,59,226), und in 16 Bit genau
                // rgb(139,59,226)). Verloren geht es auf dem **Weg dorthin**: der
                // Strom liegt als yuva420p da, und über gepacktes rgba kommt
                // rgb(136,56,225) heraus, über Ebenen rgb(138,58,225). Ein Würfel
                // vergrössert jeden Rundungsschritt davor.
                //
                // In Ebenen zu verlangen lässt den Graphen einen verlustfreieren Weg
                // aushandeln -- bei einer Farbquelle sogar ganz ohne YUV. Gemessen am
                // ganzen Bild: die Szene „look“ des Film-Vergleichs fällt von 8 auf 4.
                //
                // `gbrap` und nicht `gbrp`: der Alphakanal muss stehenbleiben, sonst
                // wird aus einer Einblendung ein deckender Kasten (dieselbe Falle wie
                // bei der Vignette, 7.140 -- dort gemessen: 128 wurde zu 255).
                const wuerfel = ['format=gbrap', `lut3d=file=${safePath(path)}`, 'format=rgba'];
                if (fx.intensity >= 1 - EPS) {
                    cur = ctx.g.pipe(cur, wuerfel);
                }
                else {
                    // Blend the graded copy back over the original to dial the LUT down.
                    const a = ctx.g.label('v');
                    const b = ctx.g.label('v');
                    ctx.g.chain([cur], ['split=2'], [a, b]);
                    const graded = ctx.g.pipe(b, wuerfel);
                    const out = ctx.g.label('v');
                    ctx.g.chain([a, graded], [`blend=all_mode=normal:all_opacity=${fmt(fx.intensity)}`], [out]);
                    cur = out;
                }
                break;
            }
        }
    }
    return cur;
}
// ---------------------------------------------------------------------------
// clip video
// ---------------------------------------------------------------------------
/** Fit-into-canvas, letterboxed, with the sequence frame rate and SAR. */
function normaliseFilters(ctx, opaque, bg) {
    return [
        `scale=${ctx.W}:${ctx.H}:force_original_aspect_ratio=decrease`,
        `pad=${ctx.W}:${ctx.H}:(ow-iw)/2:(oh-ih)/2:color=${opaque ? color(bg) : 'black@0'}`,
        'setsar=1',
        `fps=${fmt(ctx.fps)}`,
    ];
}
/**
 * Bildstabilisierung (S12), zwei Durchgänge.
 *
 * `vidstab` vermisst im ersten Durchgang die Bewegung und schreibt sie in eine
 * Datei; der zweite gleicht sie aus. Das geht nicht in einem Aufruf, also gibt
 * der Bauplan einen **Vorlauf** mit, den der Arbeitsdienst vorher startet.
 *
 * Der Vorlauf muss genau dieselben Bilder sehen wie der Hauptlauf -- dieselbe
 * Stelle in der Datei, dieselbe Grösse, dieselbe Bildrate. Darum steht hier
 * dieselbe Filterliste wie dort, und darum sitzt die Stabilisierung direkt
 * hinter dem Einpassen und nicht bei den übrigen Effekten: Tempo (S6) und
 * Keyframes verbögen die Zeit, und die Zahlen aus dem Vorlauf passten nicht
 * mehr zu den Bildern.
 *
 * `optzoom=1` zoomt so weit hinein, dass die Ränder nicht wackeln -- sonst
 * schiebt sich bei jedem Ausgleich ein schwarzer Streifen ins Bild.
 *
 * *Warum nicht `deshake`?* Das braucht nur einen Durchgang, ist aber schwach:
 * an einer gebauten Wackelaufnahme gemessen 16 % ruhiger, `vidstab` 81 %.
 */
function stabiFilter(ctx, clip, pfad, srcLen, opaque, bg) {
    const fx = clip.effects.find((e) => e.type === 'stabi');
    if (!fx || fx.type !== 'stabi')
        return [];
    const trf = `${ctx.o.workDir}/stab-${safeName(clip.id)}.trf`;
    const wackel = Math.round(2 + fx.staerke * 8);
    const glatt = Math.round(5 + fx.staerke * 25);
    ctx.vorlaeufe.push({
        zweck: `Bewegung vermessen für Clip ${clip.id}`,
        args: [
            '-hide_banner', '-nostdin', '-y', '-v', 'error',
            '-ss', fmt(clip.sourceIn), '-t', fmt(srcLen), '-i', safePath(pfad),
            '-vf', [...normaliseFilters(ctx, opaque, bg), `vidstabdetect=shakiness=${wackel}:accuracy=15:result=${safePath(trf)}`].join(','),
            '-f', 'null', '-',
        ],
    });
    return [`vidstabtransform=input=${safePath(trf)}:smoothing=${glatt}:optzoom=1`];
}
/** Eine Form, in der die Ebene läuft (7.82) -- nur auf oberen Spuren. */
function hatMaske(clip) {
    return !!clip.maske && clip.maske.form !== 'keine';
}
/**
 * Build the video for one clip, normalised to the sequence canvas and starting
 * at PTS 0. `opaque` is true for base-track segments (which must be yuv420p for
 * xfade/concat) and false for overlays (which keep an alpha channel).
 */
function buildClipVideo(ctx, clip, opaque) {
    const bg = ctx.o.timeline.settings.background;
    // Mit einer Rampe (S6) ist die Quelllänge die Fläche unter der Tempokurve --
    // ohne Rampe dasselbe wie `dauer * tempo`.
    const srcLen = quellLaenge(clip.duration, rampeVon(clip));
    let label;
    // A base-track video that was moved, scaled or turned on the canvas: build
    // it like an overlay (transparent letterbox, keyframes and all) and set it
    // onto the background colour, which gives an opaque frame again.
    //
    // Eine Bauchbinde auf der Basisspur ebenso (7.178): bis zum 29. September
    // stand sie im Film immer an der Stelle der Vorlage, in voller Grösse und
    // ungedreht -- die Vorschau schob, skalierte und drehte sie wie jede Ebene.
    const markeBewegt = clip.source.kind === 'brand' && movesOnCanvas(clip);
    // Text auf der Basisspur immer (7.182): mittig auf seiner Ebene, die Ebene
    // mit ihrer Mitte auf x/y -- sonst stand er eine halbe Texthöhe zu tief
    // und liess sich weder skalieren noch drehen.
    const textAufGrund = clip.source.kind === 'text';
    if (opaque && ((clip.source.kind === 'asset' && movesOnCanvas(clip)) || markeBewegt || textAufGrund)) {
        // Ohne Form: die Basisspur hat keine (7.82), auch wenn ein Clip eine
        // von einer oberen Spur mitbringt -- die Leinwand malt sie dort nicht.
        const layer = buildClipVideo(ctx, { ...clip, maske: null }, false);
        if (!layer)
            return null;
        const ground = ctx.g.source([
            `color=c=${color(bg)}:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}:d=${fmt(clip.duration)}`,
            'setsar=1',
            'format=yuv420p',
        ]);
        const pos = overlayPosition(clip, 'none', 'clip', markeVon(ctx, clip));
        const out = ctx.g.label('v');
        ctx.g.chain([ground, layer], [`overlay=x='${pos.x}':y='${pos.y}':shortest=1:format=auto`, 'format=yuv420p'], [out]);
        return out;
    }
    switch (clip.source.kind) {
        case 'asset': {
            const asset = ctx.o.assets.get(clip.source.assetId);
            if (!asset) {
                ctx.warnings.push(`Clip ${clip.id} skipped: asset ${clip.source.assetId} is not available.`);
                return null;
            }
            if (asset.kind === 'audio')
                return null;
            const idx = asset.kind === 'image'
                ? addFileInput(ctx, safePath(asset.path), ['-loop', '1', '-t', fmt(srcLen), '-framerate', fmt(ctx.fps)])
                : addFileInput(ctx, safePath(asset.path), ['-ss', fmt(clip.sourceIn), '-t', fmt(srcLen)]);
            label = ctx.g.pipe(`${idx}:v`, [
                // HDR ganz am Anfang (S17): erst in die gewöhnliche Welt rechnen,
                // dann einpassen, dann alles Weitere. Wer später umrechnet, hat
                // vorher auf Werten gearbeitet, die im falschen Lichtraum liegen.
                ...(asset.hdr ? HDR_NACH_SDR : []),
                ...normaliseFilters(ctx, opaque, bg),
                ...stabiFilter(ctx, clip, asset.path, srcLen, opaque, bg),
            ]);
            break;
        }
        case 'color':
            label = ctx.g.source([
                `color=c=${color(clip.source.color)}:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}:d=${fmt(clip.duration)}`,
                'setsar=1',
            ]);
            break;
        case 'text': {
            const style = clip.source.style;
            if (style.boxColor && !opaque) {
                // **Der Kasten mit runden Ecken** (7.190; Sascha, 3. Oktober: „ok
                // rund“). `drawtext` kann nur eckige Kästen. Also zuerst der Kasten
                // allein als weisse Maske, deren Ecken eine leichte Unschärfe mit
                // einem Schnitt bei der Hälfte rundet -- die geraden Kanten bleiben,
                // wo sie sind. Darauf die Farbe des Kastens und darüber die Schrift.
                const schriftPx = Math.max(8, Math.round(style.size * ctx.H));
                const sigma = Math.max(1, kastenRadius(schriftPx) / 1.5);
                const lage = { text: clip.source.text, style, x: clip.transform.x, y: clip.transform.y, mittig: true };
                const maske = ctx.g.source([
                    `color=c=black:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}:d=${fmt(clip.duration)}`,
                    'setsar=1',
                    'format=yuv420p',
                    drawText(ctx, { ...lage, nurKasten: true }),
                    'format=gray',
                    `gblur=sigma=${fmt(sigma)}`,
                    "lut=y='clip((val-128)*4+128,0,255)'",
                ]);
                const farbe = ctx.g.source([
                    `color=c=${color(style.boxColor)}:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}:d=${fmt(clip.duration)}`,
                    'setsar=1',
                    'format=rgba',
                ]);
                const kasten = ctx.g.label('v');
                ctx.g.chain([farbe, maske], ['alphamerge', `colorchannelmixer=aa=${fmt(style.boxOpacity)}`], [kasten]);
                label = ctx.g.pipe(kasten, [drawText(ctx, { ...lage, ohneKasten: true }), 'format=yuva420p']);
                break;
            }
            label = ctx.g.source([
                `color=c=${opaque ? color(bg) : 'black@0'}:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}:d=${fmt(clip.duration)}`,
                'setsar=1',
                opaque ? 'format=yuv420p' : 'format=yuva420p',
            ]);
            label = ctx.g.pipe(label, [
                // The clip's own position, not a fixed one. It used to be 0.5/0.44
                // here and 0.5/0.5 in the preview: the two disagreed with each other,
                // and neither could be moved.
                drawText(ctx, {
                    text: clip.source.text,
                    style: clip.source.style,
                    x: clip.transform.x,
                    y: clip.transform.y,
                    // Als Ebene (nicht deckend) mittig -- die Lage kommt vom Overlay.
                    mittig: !opaque,
                }),
            ]);
            break;
        }
        case 'brand': {
            const built = buildBrandLayer(ctx, clip, opaque);
            if (!built)
                return null;
            label = built;
            break;
        }
    }
    label = ctx.g.pipe(label, [opaque ? 'format=yuv420p' : 'format=yuva420p']);
    if ((clip.speed !== 1 || hatRampe(clip)) && clip.source.kind === 'asset') {
        // `setpts` rechnet auf den Zeitstempeln der **Quelle**; der Ausdruck macht
        // daraus die des Films. Ohne Rampe bleibt es bei der einfachen Teilung.
        label = ctx.g.pipe(label, [`setpts=${setptsAusdruck(clip.duration, rampeVon(clip))}`]);
    }
    label = applyEffects(ctx, label, clip);
    if (!opaque) {
        // Die Form, in der die Ebene läuft (7.82), und zwar *vor* Grösse und
        // Drehung, wie auf der Leinwand (7.183): danach mass `geq` die gedrehte
        // Hülle statt der Ebene, und die Form drehte nicht mit. Und mal genommen
        // statt ersetzt -- ersetzt wurden die durchsichtigen Ecken der Drehung
        // schwarz, und Text verlor seinen durchsichtigen Grund. Der Ausdruck
        // rechnet selbst mit `W`/`H`: die wahre Grösse der Ebene steht hier noch
        // nicht fest, sie behält das Seitenverhältnis ihrer Quelle.
        const a = hatMaske(clip) ? geqAlpha(clip.maske.form, clip.maske.weich, clip.maske) : null;
        const kontur = a && clip.maske.kontur && clip.maske.kontur.staerke > 0 ? clip.maske.kontur : null;
        if (a) {
            // **Die Form einmal, nicht auf jedem Bild** (7.236). `geq` rechnet
            // jeden Bildpunkt jedes Bildes, und die Form steht still: Lage und
            // Grösse der Maske haben keine Keyframes, und die Ebene behält ihre
            // Grösse bis hierher. Also wird sie auf dem ersten Bild gerechnet und
            // stehen gelassen (`blend` wiederholt das letzte Bild seines zweiten
            // Eingangs). Die Wolke mit Kontur brauchte im Bau so mehr als vier
            // Minuten für vier Sekunden Film; so sind es Sekunden, Bildpunkt für
            // Bildpunkt dasselbe. Mal genommen statt ersetzt, wie zuvor: Text
            // behält seinen durchsichtigen Grund.
            //
            // **Die Kontur** (7.224): die ganze Form in der Farbe der Kontur, und
            // darauf das Bild, um ihre Stärke nach innen gerückt.
            const formen = kontur ? [a, geqAlpha(clip.maske.form, clip.maske.weich, clip.maske, kontur.staerke)] : [a];
            const n = formen.length;
            const zweige = ctx.g.split(ctx.g.pipe(label, ['format=gbrap']), n + 1);
            const deckung = ctx.g.split(ctx.g.pipe(zweige[n], ['alphaextract']), n + 1);
            const erstes = ctx.g.split(ctx.g.pipe(deckung[n], ['trim=end_frame=1']), n);
            const gedeckt = formen.map((form, k) => {
                const out = ctx.g.label('v');
                ctx.g.chain([deckung[k], ctx.g.pipe(erstes[k], [`geq=lum='${form}'`])], ['blend=all_mode=multiply'], [out]);
                return out;
            });
            const mit = (bild, deckt) => {
                const out = ctx.g.label('v');
                ctx.g.chain([bild, deckt], ['alphamerge'], [out]);
                return out;
            };
            if (kontur) {
                const [r, g, b] = [1, 3, 5].map((i) => parseInt(kontur.farbe.slice(i, i + 2), 16));
                const strich = mit(ctx.g.pipe(zweige[0], [`lutrgb=r=${r}:g=${g}:b=${b}`]), gedeckt[0]);
                const zusammen = ctx.g.label('v');
                ctx.g.chain([strich, mit(zweige[1], gedeckt[1])], ['overlay=format=auto'], [zusammen]);
                label = zusammen;
            }
            else
                label = mit(zweige[0], gedeckt[0]);
        }
        // Keyframes turn size and angle into expressions in the clip's own time
        // (this runs before the segment is shifted to its slot). A field that
        // does not move keeps the plain filter, which is cheaper.
        const at0 = transformAt(clip, 0);
        const scaleExpr = keyframeExpression(clip, 'scale', 't');
        const rotateExpr = keyframeExpression(clip, 'rotate', 't');
        const drehen = (l) => {
            if (rotateExpr) {
                // The canvas has to hold every angle on the way, so it is the diagonal.
                return ctx.g.pipe(l, [`rotate=a='(${rotateExpr})*PI/180':c=black@0:ow='hypot(iw,ih)':oh='hypot(iw,ih)'`]);
            }
            if (at0.rotate !== 0) {
                const rad = (at0.rotate * Math.PI) / 180;
                return ctx.g.pipe(l, [`rotate=${fmt(rad)}:c=black@0:ow=rotw(${fmt(rad)}):oh=roth(${fmt(rad)})`]);
            }
            return l;
        };
        if (scaleExpr && (rotateExpr || at0.rotate !== 0)) {
            // Eine Grösse, die sich bewegt, an einer gedrehten Ebene (7.184): erst
            // drehen, dann die Hülle skalieren. Andersherum behielt `rotate` die
            // Grösse des ersten Bildes, und die Ebene wuchs im Film nicht mit --
            // auf halbem Weg von 0,4 auf 0,5 stand sie bei 0,41 statt 0,45.
            label = drehen(label);
            label = ctx.g.pipe(label, [`scale=w='trunc(iw*(${scaleExpr})/2)*2':h=-2:eval=frame`, 'setsar=1']);
        }
        else {
            if (scaleExpr) {
                label = ctx.g.pipe(label, [`scale=w='trunc(${ctx.W}*(${scaleExpr})/2)*2':h=-2:eval=frame`, 'setsar=1']);
            }
            else if (at0.scale !== 1) {
                label = ctx.g.pipe(label, [`scale=${Math.round(ctx.W * at0.scale)}:-2`, 'setsar=1']);
            }
            label = drehen(label);
        }
        // Die Deckkraft aus dem Regler, nicht aus dem ersten Keyframe (7.185):
        // ein Keyframe nimmt sie nur mit, wenn er gesetzt wird, und der Regler
        // schreibt sie nicht hinein. Wer sie danach änderte, sah es in der
        // Vorschau und nicht im Film.
        const deckung = clip.transform.opacity;
        if (deckung < 1) {
            label = ctx.g.pipe(label, [`colorchannelmixer=aa=${fmt(deckung)}`]);
        }
    }
    const fades = [];
    if (clip.fadeIn > 0)
        fades.push(`fade=t=in:st=0:d=${fmt(clip.fadeIn)}${opaque ? '' : ':alpha=1'}`);
    if (clip.fadeOut > 0) {
        const st = Math.max(0, clip.duration - clip.fadeOut);
        fades.push(`fade=t=out:st=${fmt(st)}:d=${fmt(clip.fadeOut)}${opaque ? '' : ':alpha=1'}`);
    }
    label = ctx.g.pipe(label, fades);
    // Trim to the exact clip length: images and generated sources can run long,
    // and xfade offsets depend on segment durations being what we think they are.
    label = ctx.g.pipe(label, [`trim=duration=${fmt(clip.duration)}`, 'setpts=PTS-STARTPTS']);
    return label;
}
function buildBrandLayer(ctx, clip, opaque) {
    if (clip.source.kind !== 'brand')
        return null;
    const kit = ctx.o.brandKit;
    const tpl = kit?.templates.find((t) => t.id === clip.source.templateId);
    if (!kit || !tpl) {
        ctx.warnings.push(`Clip ${clip.id} skipped: brand template "${clip.source.templateId}" not found.`);
        return null;
    }
    const bg = ctx.o.timeline.settings.background;
    let label = ctx.g.source([
        `color=c=${opaque ? color(bg) : 'black@0'}:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}:d=${fmt(clip.duration)}`,
        'setsar=1',
        opaque ? 'format=yuv420p' : 'format=yuva420p',
    ]);
    for (const layer of tpl.layers) {
        if (layer.type === 'rect') {
            // Not drawbox: on a transparent frame it writes colour into the luma and
            // chroma planes but leaves alpha at zero, so the box never composites.
            // A real colour source carries its own alpha and overlays correctly.
            const w = Math.max(2, Math.round(layer.w * ctx.W));
            const h = Math.max(2, Math.round(layer.h * ctx.H));
            const rect = ctx.g.source([
                `color=c=${color(resolveBrandColor(kit, layer.color))}:s=${w}x${h}:r=${fmt(ctx.fps)}:d=${fmt(clip.duration)}`,
                'setsar=1',
                'format=yuva420p',
                `colorchannelmixer=aa=${fmt(layer.opacity)}`,
            ]);
            const out = ctx.g.label('v');
            ctx.g.chain([label, rect], [`overlay=x=${Math.round(layer.x * ctx.W)}:y=${Math.round(layer.y * ctx.H)}:eof_action=repeat:format=auto`], [out]);
            label = out;
        }
        else if (layer.type === 'text') {
            const value = clip.source.fields[layer.slot] ?? layer.placeholder;
            if (!value.trim())
                continue;
            const style = {
                ...layer.style,
                fontFamily: layer.style.fontFamily || kit.fontFamily,
                // A layer that references the kit's colour wins over its own literal.
                color: layer.color ? resolveBrandColor(kit, layer.color) : layer.style.color,
            };
            label = ctx.g.pipe(label, [
                drawText(ctx, {
                    text: value,
                    style,
                    x: layer.x,
                    y: layer.y,
                    fontFile: kit.fontPath ?? ctx.o.fontFile,
                }),
            ]);
        }
        else if (layer.type === 'logo') {
            if (!kit.logoPath) {
                ctx.warnings.push(`Brand template "${tpl.id}": no logo uploaded, logo layer skipped.`);
                continue;
            }
            const idx = addCachedFileInput(ctx, safePath(kit.logoPath));
            const logo = ctx.g.pipe(`${idx}:v`, [
                `scale=${Math.round(layer.w * ctx.W)}:-2`,
                'format=yuva420p',
                `colorchannelmixer=aa=${fmt(layer.opacity)}`,
            ]);
            const out = ctx.g.label('v');
            ctx.g.chain([label, logo], [`overlay=x=${Math.round(layer.x * ctx.W)}:y=${Math.round(layer.y * ctx.H)}:eof_action=repeat:format=auto`], [out]);
            label = out;
        }
    }
    return label;
}
// ---------------------------------------------------------------------------
// base track
// ---------------------------------------------------------------------------
/**
 * **Der Weg durch Schwarz (oder Weiss) rechnet in RGB** (7.149).
 *
 * Eine Mischung *zwischen zwei Farben* ist in YUV und in RGB dieselbe -- die
 * Umrechnung ist affin, und eine Gerade bleibt eine Gerade. Darum stimmen
 * Blende und Wischkanten auf beiden Wegen. Beim Weg durch eine dritte Farbe
 * gilt das nicht: `xfade` mischt auf einem yuv420p-Strom gegen Y=0, und das
 * ist kein schwarzes Bild, sondern eines *unter* Schwarz.
 *
 * Gemessen am 22. September an rgb(30,122,60), ein Zehntel in die Blende:
 * der Film rgb(2,46,15), gerechnet (und auf Ebenen gemessen) rgb(14,55,26).
 * Dieselbe Falle wie bei der Vignette (7.140), dem Look (7.141) und den
 * Farbrädern (7.146) -- die vierte.
 */
const DURCH_EINE_FARBE = new Set(['dipToBlack', 'dipToWhite']);
const XFADE_NAMES = {
    crossfade: 'fade',
    dipToBlack: 'fadeblack',
    dipToWhite: 'fadewhite',
    wipeleft: 'wipeleft',
    wiperight: 'wiperight',
    wipeup: 'wipeup',
    wipedown: 'wipedown',
    slideleft: 'slideleft',
    slideright: 'slideright',
    circleopen: 'circleopen',
    circleclose: 'circleclose',
    horzopen: 'horzopen',
    vertopen: 'vertopen',
    diagbr: 'diagbr',
    diagtl: 'diagtl',
    radial: 'radial',
    pixelize: 'pixelize',
};
/**
 * Die drei Stufen eines Exports (7.213). `crf` ist die Qualitätszahl von x264
 * -- kleiner heisst besser und grösser; ab etwa 12 ist kein Unterschied zum
 * Original mehr zu sehen. Wirklich verlustfrei (crf 0) spielt kein Telefon ab,
 * deshalb ist „Master“ visuell verlustfrei und nicht mathematisch.
 */
export const EXPORT_STUFEN = {
    standard: { preset: 'medium', crf: 19, ton: '192k' },
    hoch: { preset: 'slow', crf: 15, ton: '256k' },
    master: { preset: 'slow', crf: 11, ton: '320k' },
};
const VORSCHAU_STUFE = { preset: 'veryfast', crf: 27, ton: '128k' };
/**
 * Das H.264-Level zur Bildgrösse und Bildrate. Fest „4.1“ hiess: höchstens
 * 1080p mit 30 Bildern -- ein Export mit 60 (7.212) sagte sich damit als
 * etwas aus, das er nicht ist, und strenge Abspieler lehnen das ab.
 */
export function h264Level(w, h, fps) {
    const makroblocke = Math.ceil(w / 16) * Math.ceil(h / 16);
    const jeSekunde = makroblocke * fps;
    if (makroblocke <= 8192 && jeSekunde <= 245760)
        return '4.1';
    if (makroblocke <= 8704 && jeSekunde <= 522240)
        return '4.2';
    if (makroblocke <= 22080 && jeSekunde <= 589824)
        return '5.1';
    if (makroblocke <= 36864 && jeSekunde <= 983040)
        return '5.1';
    return '5.2';
}
function aneinander(ctx) {
    return ['concat=n=2:v=1:a=0', `fps=${fmt(ctx.fps)}`];
}
function blackSegment(ctx, duration) {
    const bg = ctx.o.timeline.settings.background;
    return ctx.g.source([
        `color=c=${color(bg)}:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}:d=${fmt(duration)}`,
        'setsar=1',
        'format=yuv420p',
    ]);
}
/**
 * Lay the base track out end to end. `clip.start` is the moment the clip
 * becomes visible; with a transition it is also where the overlap with the
 * previous clip begins, so the previous clip runs on until `start + duration`
 * of the transition and the composed length after the join is
 * `clip.start + clip.duration`.
 */
function buildBaseTrack(ctx, track, total) {
    const clips = track ? sortedClips(track).filter((c) => !track.hidden) : [];
    if (clips.length === 0) {
        return { label: blackSegment(ctx, Math.max(total, 0.04)), duration: Math.max(total, 0.04) };
    }
    let cur = null;
    let acc = 0;
    for (const clip of clips) {
        const seg = buildClipVideo(ctx, clip, true);
        if (!seg)
            continue;
        if (cur === null) {
            if (clip.start > EPS) {
                cur = blackSegment(ctx, clip.start);
                acc = clip.start;
                const out = ctx.g.label('v');
                ctx.g.chain([cur, seg], aneinander(ctx), [out]);
                cur = out;
            }
            else {
                cur = seg;
            }
            acc = clip.start + clip.duration;
            continue;
        }
        const trans = clip.transitionIn;
        // Der Übergang ist die Überlappung (7.89). Gerechnet wird nur mit der, die
        // wirklich dasteht: bis zum 15. September zog dieser Zweig das *Bild* des
        // Clips um die Länge des Übergangs nach vorn, während sein Ton auf der
        // Zeit der Zeitleiste liegen blieb -- das Bild lief seinem Ton davon, und
        // am Ende blieb Schwarz stehen. Hergestellt wird die Überlappung jetzt
        // beim Setzen (`ueberlappungHerstellen`), und hier wird sie nur benutzt.
        const ueberlappung = acc - clip.start;
        if (trans && trans.duration > EPS && ueberlappung > EPS) {
            // xfade needs the first stream to reach offset + duration.
            const offset = Math.max(0, clip.start);
            const d = Math.min(trans.duration, clip.duration, ueberlappung);
            const out = ctx.g.label('v');
            // Auf Ebenen, wenn der Übergang durch eine Farbe geht (7.149).
            const aufEbenen = DURCH_EINE_FARBE.has(trans.kind);
            const eingang = aufEbenen ? [ctx.g.pipe(cur, ['format=gbrp']), ctx.g.pipe(seg, ['format=gbrp'])] : [cur, seg];
            ctx.g.chain(eingang, [
                `xfade=transition=${XFADE_NAMES[trans.kind]}:duration=${fmt(d)}:offset=${fmt(offset)}`,
                ...(aufEbenen ? ['format=yuv420p'] : []),
                `fps=${fmt(ctx.fps)}`,
            ], [out]);
            cur = out;
            acc = offset + clip.duration;
            continue;
        }
        if (trans && trans.duration > EPS) {
            // Ein Übergang ohne Überlappung: so sind Schnitte aus der Zeit vor 7.89
            // gespeichert. Hier wird hart geschnitten statt heimlich verschoben --
            // und es steht in den Hinweisen zum Export, damit es nicht stillschweigend
            // geschieht. Ein einziges Neusetzen im Blatt „Übergänge“ richtet es.
            ctx.warnings.push(`Clip ${clip.id}: der Übergang sitzt nicht, weil er den vorigen Clip nicht überlappt. Im Blatt „Übergänge“ einmal neu setzen.`);
        }
        const gap = clip.start - acc;
        if (gap > EPS) {
            const filler = blackSegment(ctx, gap);
            const out = ctx.g.label('v');
            ctx.g.chain([cur, filler], aneinander(ctx), [out]);
            cur = out;
            acc += gap;
        }
        else if (gap < -EPS) {
            ctx.warnings.push(`Clip ${clip.id} overlaps the previous clip on the base track without a transition; it was moved to ${fmt(acc)}s.`);
        }
        const out = ctx.g.label('v');
        ctx.g.chain([cur, seg], aneinander(ctx), [out]);
        cur = out;
        acc += clip.duration;
    }
    if (cur === null) {
        const d = Math.max(total, 0.04);
        return { label: blackSegment(ctx, d), duration: d };
    }
    if (total - acc > EPS) {
        const filler = blackSegment(ctx, total - acc);
        const out = ctx.g.label('v');
        ctx.g.chain([cur, filler], aneinander(ctx), [out]);
        cur = out;
        acc = total;
    }
    return { label: cur, duration: acc };
}
// ---------------------------------------------------------------------------
// overlays
// ---------------------------------------------------------------------------
/**
 * Wie weit die Mitte des Inhalts einer Markenebene neben der Bildmitte liegt
 * (7.19a) -- dieselbe Schätzung wie in der Vorschau. Für alles andere nichts.
 */
function markenVersatzVon(ctx, clip) {
    if (clip.source.kind !== 'brand')
        return null;
    const kit = ctx.o.brandKit;
    const tpl = kit?.templates.find((t) => t.id === clip.source.templateId);
    if (!kit || !tpl)
        return null;
    return markenVersatz(markenKasten(tpl, clip.source.fields, ctx.W / ctx.H, Boolean(kit.logoPath)));
}
function markeVon(ctx, clip) {
    const versatz = markenVersatzVon(ctx, clip);
    return versatz ? { versatz, seiten: ctx.W / ctx.H } : null;
}
function overlayPosition(clip, animation, origin = 'timeline', marke = null) {
    // The overlay filter's t is timeline time when the layer was shifted to
    // its slot, and clip time when it is composed on its own; keyframes count
    // from the clip either way.
    const s = origin === 'timeline' ? fmt(clip.start) : '0';
    const at0 = transformAt(clip, 0);
    const xExpr = keyframeExpression(clip, 'x', `t-${s}`);
    const yExpr = keyframeExpression(clip, 'y', `t-${s}`);
    // Eine Markenebene dreht und skaliert um die Mitte ihres Inhalts, nicht um
    // die des Bildes (7.19a). Das Bild selbst dreht `rotate=` weiter um seine
    // Mitte; was fehlt, ist der Schub `v - s·R·v` (siehe `markenLage`).
    let ox = '';
    let oy = '';
    if (marke) {
        const scaleExpr = keyframeExpression(clip, 'scale', `t-${s}`);
        const rotateExpr = keyframeExpression(clip, 'rotate', `t-${s}`);
        if (!scaleExpr && !rotateExpr) {
            const o = markenLage(0, 0, at0.scale, at0.rotate, marke.versatz, marke.seiten);
            ox = `+(${fmt(o.x)})`;
            oy = `+(${fmt(o.y)})`;
        }
        else {
            const sk = scaleExpr ? `(${scaleExpr})` : fmt(at0.scale);
            const a = rotateExpr ? `((${rotateExpr})*PI/180)` : fmt((at0.rotate * Math.PI) / 180);
            const vx = fmt(marke.versatz.dx * marke.seiten);
            const vy = fmt(marke.versatz.dy);
            ox = `+(${vx}-${sk}*(cos(${a})*${vx}-sin(${a})*${vy}))/${fmt(marke.seiten)}`;
            oy = `+(${vy}-${sk}*(sin(${a})*${vx}+cos(${a})*${vy}))`;
        }
    }
    const bx = xExpr ? `(${xExpr})` : fmt(at0.x);
    const by = yExpr ? `(${yExpr})` : fmt(at0.y);
    const cx = marke ? `main_w*(${bx}${ox})-overlay_w/2` : `main_w*${bx}-overlay_w/2`;
    const cy = marke ? `main_h*(${by}${oy})-overlay_h/2` : `main_h*${by}-overlay_h/2`;
    const dur = 0.4;
    // Each slide starts a little off its final place and settles into it. The
    // sign says which side it comes from.
    const slideX = (factor) => `${cx}+if(lt(t-${s},${fmt(dur)}),(1-(t-${s})/${fmt(dur)})*main_w*${fmt(factor)},0)`;
    const slideY = (factor) => `${cy}+if(lt(t-${s},${fmt(dur)}),(1-(t-${s})/${fmt(dur)})*main_h*${fmt(factor)},0)`;
    if (animation === 'slideLeft')
        return { x: slideX(0.08), y: cy };
    if (animation === 'slideRight')
        return { x: slideX(-0.08), y: cy };
    if (animation === 'slideUp')
        return { x: cx, y: slideY(0.06) };
    if (animation === 'slideDown')
        return { x: cx, y: slideY(-0.06) };
    return { x: cx, y: cy };
}
/** Zwei Ströme durch einen Filter, mit einem frischen Ausgang. */
function zusammen(ctx, a, b, filters) {
    const out = ctx.g.label('v');
    ctx.g.chain([a, b], filters, [out]);
    return out;
}
/**
 * Eine Ebene auf eine durchsichtige volle Fläche setzen -- an ihrer Stelle.
 *
 * Gebraucht wird das zweimal: beim Mischen (S10) und beim Übergang auf einer
 * oberen Spur. Dort müssen zwei Ebenen gegeneinander überblendet werden, und
 * das geht nur, wenn beide dasselbe Mass haben; ihre Stelle steckt danach im
 * Bild statt im `overlay`.
 */
function aufVolleFlaeche(ctx, ebene, pos) {
    const leer = ctx.g.source([`color=c=black@0:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}`, 'format=rgba']);
    const voll = ctx.g.label('v');
    // `format` kennt bei `overlay` nur yuv/rgb/auto -- rgba wird danach gesetzt,
    // sonst lehnt ffmpeg die ganze Kette ab.
    ctx.g.chain([leer, ebene], [`overlay=x='${pos.x}':y='${pos.y}':shortest=1:format=auto`, 'format=rgba'], [voll]);
    return voll;
}
/**
 * Hängen diese beiden Clips mit einem Übergang zusammen?
 *
 * Beides muss stimmen: ein gesetzter Übergang am hinteren *und* eine
 * Überlappung, aus der er gerechnet werden kann. Ein Übergang ohne
 * Überlappung ist ein Schnitt aus der Zeit vor 7.89 -- der wird gemeldet,
 * nicht heimlich zurechtgerückt.
 */
function verbunden(vor, dieser) {
    const d = dieser.transitionIn?.duration ?? 0;
    return d > EPS && vor.start + vor.duration > dieser.start + EPS;
}
/**
 * Die oberen Bildspuren auf das Bild darunter legen.
 *
 * **Übergänge gibt es hier genauso wie unten** (18. September). Sascha:
 * „jedes andere schnittprogramm kann das auch überall / Mit den Übergängen“.
 * Er hat recht, und der Grund dagegen war ein technischer: unten werden die
 * Clips hintereinandergelegt und mit `xfade` verbunden, oben wird jeder
 * einzeln auf das Bild darunter gelegt -- zwischen zwei davon gibt es nichts
 * zu überblenden.
 *
 * Es gibt aber doch etwas: zwei Ebenen, jede auf einer durchsichtigen vollen
 * Fläche. `xfade` rechnet auch mit Alpha (nachgemessen: an einer Stelle, an
 * der beide Ebenen durchsichtig sind, bleibt sie durchsichtig). Also werden
 * Clips, die ein Übergang verbindet, zu *einer* Ebene zusammengerechnet --
 * mit denselben siebzehn Arten wie unten -- und erst danach aufgelegt.
 *
 * Clips ohne Übergang bleiben einzeln, und das ist kein Versäumnis: auf einer
 * oberen Spur liegt oft ein Titel *innerhalb* einer Bauchbinde. Die beiden
 * lösen einander nicht ab, sie liegen übereinander.
 */
function buildOverlays(ctx, base, tracks) {
    let cur = base;
    for (const track of tracks) {
        if (track.hidden)
            continue;
        const clips = sortedClips(track);
        const modus = track.mischmodus ?? 'normal';
        let i = 0;
        while (i < clips.length) {
            let bis = i;
            while (bis + 1 < clips.length && verbunden(clips[bis], clips[bis + 1]))
                bis += 1;
            if (bis > i) {
                const gruppe = ebenenGruppe(ctx, clips.slice(i, bis + 1));
                if (gruppe) {
                    cur =
                        modus !== 'normal'
                            ? mische(ctx, cur, gruppe, { x: '0', y: '0' }, modus)
                            : zusammen(ctx, cur, gruppe, [`overlay=x='0':y='0':eof_action=pass:format=auto`]);
                }
                i = bis + 1;
                continue;
            }
            const seg = einzelneEbene(ctx, clips[i]);
            if (seg) {
                cur =
                    modus !== 'normal'
                        ? mische(ctx, cur, seg.label, seg.pos, modus)
                        : zusammen(ctx, cur, seg.label, [`overlay=x='${seg.pos.x}':y='${seg.pos.y}':eof_action=pass:format=auto`]);
            }
            i += 1;
        }
    }
    return cur;
}
/** Eine Ebene, fertig für ihren Platz: verschoben, mit Maske und Anflug. */
function einzelneEbene(ctx, clip) {
    const roh = ebeneOhneVersatz(ctx, clip);
    if (!roh)
        return null;
    let seg = roh.label;
    // Shift the layer to its slot on the timeline by padding transparent
    // frames in front of it; `eof_action=pass` lets the base run on after.
    if (clip.start > EPS) {
        seg = ctx.g.pipe(seg, [`tpad=start_duration=${fmt(clip.start)}:start_mode=add:color=black@0`]);
    }
    seg = ctx.g.pipe(seg, ['setpts=PTS-STARTPTS']);
    return { label: seg, pos: overlayPosition(clip, roh.animation, 'timeline', markeVon(ctx, clip)) };
}
/**
 * Mehrere Clips einer oberen Spur, die Übergänge verbinden, als *eine* Ebene.
 *
 * Gerechnet wird wie unten auf der Basisspur: jeder Clip fängt bei 0 an, und
 * `xfade` setzt ihn mit seinem Versatz zum Anfang der Gruppe dahinter. Der
 * Unterschied ist, dass jeder vorher an seine Stelle auf eine durchsichtige
 * volle Fläche kommt -- sonst hätten die beiden Ströme nicht dasselbe Mass,
 * und die Stelle des zweiten ginge beim Überblenden verloren.
 */
function ebenenGruppe(ctx, clips) {
    const gruppeStart = clips[0].start;
    let cur = null;
    let acc = 0;
    for (const clip of clips) {
        const roh = ebeneOhneVersatz(ctx, clip);
        if (!roh)
            continue;
        const flaeche = aufVolleFlaeche(ctx, roh.label, overlayPosition(clip, roh.animation, 'clip', markeVon(ctx, clip)));
        if (cur === null) {
            cur = flaeche;
            acc = clip.duration;
            continue;
        }
        const offset = Math.max(0, clip.start - gruppeStart);
        const d = Math.max(0, Math.min(clip.transitionIn?.duration ?? 0, clip.duration, acc - offset));
        if (d <= EPS) {
            // Kann nicht vorkommen -- `verbunden` hat beides geprüft. Wenn doch,
            // wird hart geschnitten und nichts heimlich verschoben.
            cur = zusammen(ctx, cur, flaeche, ['overlay=eof_action=pass:format=auto']);
            acc = Math.max(acc, offset + clip.duration);
            continue;
        }
        const out = ctx.g.label('v');
        const art = clip.transitionIn.kind;
        if (art === 'crossfade' || art === 'dipToBlack' || art === 'dipToWhite' || art === 'pixelize') {
            // **Überblenden auf einer oberen Spur** (7.187): der abgelöste Clip
            // bleibt stehen, und der neue blendet darüber auf -- wie in der
            // Vorschau und unten auf der Basisspur. `xfade` mischte die beiden
            // Flächen samt ihrem durchsichtigen Rand, und der zählt dort als
            // Schwarz: mitten in der Blende waren beide Ebenen halb dunkel.
            // Der Weg durch Schwarz oder Weiss ebenso (7.188): `fadeblack` machte
            // auf einer oberen Spur das *ganze* Bild schwarz, samt der Spuren
            // darunter; die Vorschau zeigt dort eine Überblendung. Und das Raster
            // (7.189): `xfade` machte beide Ebenen dunkel wie bei der Überblendung.
            // Die gröberen Punkte des neuen Clips, die die Vorschau dabei zeigt,
            // fehlen hier -- ffmpeg 5.1 hat kein `pixelize`.
            const bis = offset + clip.duration;
            const alt = bis > acc + EPS ? ctx.g.pipe(cur, [`tpad=stop_duration=${fmt(bis - acc)}:stop_mode=add:color=black@0`]) : cur;
            const neu = ctx.g.pipe(flaeche, [
                `tpad=start_duration=${fmt(offset)}:start_mode=add:color=black@0`,
                `fade=t=in:st=${fmt(offset)}:d=${fmt(d)}:alpha=1`,
            ]);
            ctx.g.chain([alt, neu], ['overlay=eof_action=pass:format=auto', `fps=${fmt(ctx.fps)}`, 'format=rgba'], [out]);
            cur = out;
            acc = Math.max(acc, bis);
            continue;
        }
        // Auf einer oberen Spur liegt der Strom schon als rgba da -- dort geht
        // die Mischung ohnehin über RGB, und `format=gbrp` wäre ein Umweg, der
        // den Alphakanal kostet (7.149).
        ctx.g.chain([cur, flaeche], [`xfade=transition=${XFADE_NAMES[clip.transitionIn.kind]}:duration=${fmt(d)}:offset=${fmt(offset)}`, `fps=${fmt(ctx.fps)}`, 'format=rgba'], [out]);
        cur = out;
        acc = offset + clip.duration;
    }
    if (cur === null)
        return null;
    let seg = cur;
    if (gruppeStart > EPS) {
        seg = ctx.g.pipe(seg, [`tpad=start_duration=${fmt(gruppeStart)}:start_mode=add:color=black@0`]);
    }
    return ctx.g.pipe(seg, ['setpts=PTS-STARTPTS']);
}
/** Der Clip als Ebene, bei 0 anfangend -- mit dem Anflug einer Vorlage (die Form setzt `buildClipVideo`). */
function ebeneOhneVersatz(ctx, clip) {
    let seg = buildClipVideo(ctx, clip, false);
    if (!seg)
        return null;
    // A brand template brings its own; anything else uses the clip's.
    let animation = clip.animation;
    if (clip.source.kind === 'brand') {
        const tpl = ctx.o.brandKit?.templates.find((t) => t.id === clip.source.templateId);
        animation = tpl?.animation ?? 'fade';
        // Templates animate themselves in unless the clip already fades.
        if (animation !== 'none' && clip.fadeIn === 0) {
            seg = ctx.g.pipe(seg, ['fade=t=in:st=0:d=0.35:alpha=1']);
        }
        if (animation !== 'none' && clip.fadeOut === 0 && clip.duration > 0.8) {
            seg = ctx.g.pipe(seg, [`fade=t=out:st=${fmt(clip.duration - 0.35)}:d=0.35:alpha=1`]);
        }
    }
    return { label: seg, animation };
}
/**
 * Eine Ebene *gemischt* statt daraufgelegt (S10).
 *
 * `overlay` kennt keine Mischmodi und `blend` keine Position: `blend` rechnet
 * Punkt für Punkt über die ganze Fläche. Also erst die Ebene an ihrer Stelle
 * auf eine durchsichtige volle Fläche legen, dann mischen -- und das Ergebnis
 * nur dort einsetzen, wo die Ebene wirklich etwas hat.
 *
 * Der letzte Schritt ist der, den man weglassen möchte und nicht darf: ohne
 * ihn rechnete „Multiplizieren“ auch dort, wo gar keine Ebene liegt -- und
 * multiplizieren mit Nichts ist Schwarz. Das Bild wäre ausserhalb der Ebene
 * schwarz statt unverändert. Dasselbe gilt für die Maske einer Ebene (7.82)
 * und für ihre Blenden: beide stecken im Alphakanal, und der entscheidet hier,
 * wie viel vom gemischten Bild überhaupt sichtbar wird.
 */
/**
 * „Weiches Licht“, wie das Stilblatt es rechnet (7.134).
 *
 * `blend=all_mode=softlight` rechnet eine *andere* Formel als CSS: gemessen
 * am 21. September ergab rgb(153,187,238) auf rgb(30,122,60) im Film
 * rgb(33, 150, 98), in der Vorschau rgb(41, 147, 115) -- siebzehn Werte im
 * blauen Kanal. `multiply` und `screen` stimmen dagegen genau.
 *
 * Die Vorschau kann ihre Formel nicht wechseln (der Browser gibt sie vor),
 * also folgt der Film -- dieselbe Richtung wie bei 7.131. Geschrieben als
 * Ausdruck: `A` ist der Untergrund, `B` die Ebene darüber, beide 0..255.
 */
export function weichesLichtAusdruck() {
    // `x` ist der Untergrund, `y` die Ebene darüber -- so heissen die beiden
    // Eingänge bei `lut2`.
    const a = 'x/255';
    const b = 'y/255';
    const d = `if(lte(${a},0.25),((16*${a}-12)*${a}+4)*${a},sqrt(${a}))`;
    const dunkel = `${a}-(1-2*${b})*${a}*(1-${a})`;
    const hell = `${a}+(2*${b}-1)*((${d})-${a})`;
    return `255*if(lte(${b},0.5),${dunkel},${hell})`;
}
/**
 * Und die Formel als Filter -- **als Tabelle, nicht als Ausdruck je Punkt**
 * (7.134).
 *
 * `blend=all_expr=` rechnet den Ausdruck für jeden Bildpunkt einzeln: gemessen
 * an 100 Bildern in 1080x1920 kostet das 41 s gegen 2,4 s mit `all_mode`, also
 * das Siebzehnfache. `lut2` rechnet daraus **einmal** eine Tabelle über beide
 * Eingänge (256 x 256 je Kanal) und sieht danach nur noch nach: gemessen
 * 0,8 s -- schneller als der eingebaute Modus, den es ersetzt, und auf einen
 * Wert genau wie die Vorschau.
 */
export function weichesLichtFilter() {
    const e = weichesLichtAusdruck();
    return `lut2=c0='${e}':c1='${e}':c2='${e}':shortest=1`;
}
/** Dieselbe Formel als Zahl, für die Prüfungen (0..255 hinein und heraus). */
export function weichesLichtAuf(unten, oben) {
    const eins = (cb, cs) => {
        const b = cb / 255;
        const s = cs / 255;
        if (s <= 0.5)
            return Math.round(255 * (b - (1 - 2 * s) * b * (1 - b)));
        const d = b <= 0.25 ? ((16 * b - 12) * b + 4) * b : Math.sqrt(b);
        return Math.round(255 * (b + (2 * s - 1) * (d - b)));
    };
    return [eins(unten[0], oben[0]), eins(unten[1], oben[1]), eins(unten[2], oben[2])];
}
function mische(ctx, unten, ebene, pos, modus) {
    const leer = ctx.g.source([`color=c=black@0:s=${ctx.W}x${ctx.H}:r=${fmt(ctx.fps)}`, 'format=rgba']);
    const voll = ctx.g.label('v');
    // `format` kennt bei `overlay` nur yuv/rgb/auto -- rgba wird danach gesetzt,
    // sonst lehnt ffmpeg die ganze Kette ab (beim ersten Lauf gesehen).
    ctx.g.chain([leer, ebene], [`overlay=x='${pos.x}':y='${pos.y}':shortest=1:format=auto`, 'format=rgba'], [voll]);
    const [zumMischen, fuerDieMaske] = ctx.g.split(voll, 2);
    const [untenA, untenB] = ctx.g.split(unten, 2);
    // `shortest=1`: nach dem Ende der Ebene hört der gemischte Strom auf, und
    // das `overlay` unten lässt den Untergrund weiterlaufen. Ohne das hinge das
    // letzte Bild der Ebene bis zum Schluss im Film.
    const gemischt = ctx.g.label('v');
    // „Weiches Licht“ geht seinen eigenen Weg (7.134): `all_mode=softlight`
    // rechnet eine andere Formel als das Stilblatt, und die Vorschau kann ihre
    // nicht wechseln. Gerechnet wird deshalb über eine Tabelle -- exakt wie die
    // Vorschau und schneller als der eingebaute Modus. `multiply` und `screen`
    // stimmen nachgemessen genau und bleiben, wie sie waren.
    //
    // `lut2` arbeitet auf Ebenen (`gbrp`), nicht auf gepackten Punkten; das
    // Alpha kommt gleich danach über `alphamerge` wieder dazu, wie beim Modus
    // auch.
    if (modus === 'softlight') {
        ctx.g.chain([ctx.g.pipe(untenA, ['format=gbrp']), ctx.g.pipe(zumMischen, ['format=gbrp'])], [weichesLichtFilter(), 'format=rgba'], [gemischt]);
    }
    else {
        ctx.g.chain([ctx.g.pipe(untenA, ['format=rgba']), zumMischen], [`blend=all_mode=${modus}:shortest=1`], [gemischt]);
    }
    const maske = ctx.g.pipe(fuerDieMaske, ['alphaextract']);
    const mitAlpha = ctx.g.label('v');
    ctx.g.chain([gemischt, maske], ['alphamerge'], [mitAlpha]);
    const out = ctx.g.label('v');
    ctx.g.chain([untenB, mitAlpha], ['overlay=eof_action=pass:format=auto'], [out]);
    return out;
}
// ---------------------------------------------------------------------------
// audio
// ---------------------------------------------------------------------------
function buildAudio(ctx, timeline, total) {
    const parts = [];
    // Where speech is audible, for the music beds that duck under it.
    const speech = speechIntervals(timeline, (id) => ctx.o.assets.get(id)?.hasAudio ?? false);
    for (const track of timeline.tracks) {
        if (track.muted)
            continue;
        for (const clip of sortedClips(track)) {
            // `gain === 0` heißt stumm -- außer der Clip hat einen Pegelverlauf,
            // der irgendwo lauter wird; dann ist die Zahl gar nicht die Auskunft.
            const punkte = clip.gainKeyframes ?? [];
            const stumm = punkte.length > 0 ? punkte.every((k) => k.gain === 0) : clip.gain === 0;
            if (clip.muted || stumm || track.gain === 0)
                continue;
            if (clip.source.kind !== 'asset')
                continue;
            const asset = ctx.o.assets.get(clip.source.assetId);
            if (!asset || !asset.hasAudio)
                continue;
            if (track.kind === 'video' && asset.kind === 'image')
                continue;
            const rampe = rampeVon(clip);
            const srcLen = quellLaenge(clip.duration, rampe);
            const idx = addFileInput(ctx, safePath(asset.path), ['-ss', fmt(clip.sourceIn), '-t', fmt(srcLen)]);
            let quelle = `${idx}:a`;
            const kopf = [
                `aformat=sample_fmts=fltp:sample_rates=${ctx.sr}:channel_layouts=stereo`,
                'asetpts=PTS-STARTPTS',
            ];
            if (!hatRampe(clip)) {
                kopf.push(...atempoChain(clip.speed));
            }
            else {
                // Eine Rampe im Ton (S6). `atempo` nimmt eine **feste** Zahl und hält
                // dabei die Tonhöhe -- genau das, was gewollt war; eine veränderliche
                // Zahl kennt es nicht. Also wird die Quelle in Stufen geschnitten,
                // jede mit dem Tempo ihrer Mitte, und danach wieder aneinandergehängt.
                // Ein Eingang und N Zweige, nicht N Eingänge: sonst öffnete ein Clip
                // von zehn Sekunden hundert Dateien.
                const stufen = tonStufen(clip.duration, rampe);
                quelle = ctx.g.pipe(quelle, kopf, 'a');
                const zweige = ctx.g.splitAudio(quelle, stufen.length);
                const fertig = stufen.map((st, i) => {
                    const vonQ = quellZeit(st.von, clip.duration, rampe);
                    const bisQ = quellZeit(st.bis, clip.duration, rampe);
                    return ctx.g.pipe(zweige[i], [`atrim=start=${fmt(vonQ)}:end=${fmt(bisQ)}`, 'asetpts=PTS-STARTPTS', ...atempoChain(st.tempo)], 'a');
                });
                quelle = ctx.g.concatAudio(fertig);
                kopf.length = 0;
            }
            const filters = kopf;
            // The track's level and the clip's own multiply, so pulling a whole
            // music bed down does not undo the balance between its clips.
            //
            // Hat der Clip einen Pegelverlauf, steht statt der Zahl der Ausdruck
            // dort -- in der Zeit des Clips, denn der Filter läuft nach asetpts.
            const verlauf = gainExpression(clip, 't');
            if (verlauf) {
                const mitSpur = track.gain === 1 ? verlauf : `(${verlauf})*${fmt(track.gain)}`;
                filters.push(`volume='${mitSpur}':eval=frame`);
            }
            else {
                const gain = clip.gain * track.gain;
                if (gain !== 1)
                    filters.push(`volume=${fmt(gain)}`);
            }
            if (track.duck) {
                // In the clip's own time: the filter runs after asetpts, so t starts
                // at zero where the clip starts.
                const local = speech.map((i) => ({ start: i.start - clip.start, end: i.end - clip.start }));
                const envelope = duckExpression(local, track.duckLevel);
                if (envelope)
                    filters.push(`volume='${envelope}':eval=frame`);
            }
            // Werkzeuge für den Ton (S19): vor den Blenden, nach dem Pegel. Ein
            // Kompressor hinter einer Ausblende würde sie wieder aufziehen.
            filters.push(...tonFilter(clip.tonEffekte ?? []));
            if (clip.fadeIn > 0)
                filters.push(`afade=t=in:st=0:d=${fmt(clip.fadeIn)}`);
            if (clip.fadeOut > 0) {
                filters.push(`afade=t=out:st=${fmt(Math.max(0, clip.duration - clip.fadeOut))}:d=${fmt(clip.fadeOut)}`);
            }
            // A transition on the video is an equal-length fade on the audio.
            if (clip.transitionIn && clip.transitionIn.duration > EPS && clip.fadeIn === 0) {
                filters.push(`afade=t=in:st=0:d=${fmt(Math.min(clip.transitionIn.duration, clip.duration))}`);
            }
            filters.push(`atrim=duration=${fmt(clip.duration)}`);
            const ms = Math.round(clip.start * 1000);
            if (ms > 0)
                filters.push(`adelay=${ms}|${ms}`);
            parts.push(ctx.g.pipe(quelle, filters, 'a'));
        }
    }
    if (parts.length === 0) {
        return ctx.g.source([`anullsrc=r=${ctx.sr}:cl=stereo:d=${fmt(total)}`], 'a');
    }
    let mixed;
    if (parts.length === 1) {
        mixed = parts[0];
    }
    else {
        mixed = ctx.g.label('a');
        ctx.g.chain(parts, [`amix=inputs=${parts.length}:normalize=0:dropout_transition=0`], [mixed]);
    }
    return ctx.g.pipe(mixed, [
        // Auf die Ziellautheit rechnen (S20) -- vor dem Begrenzer: `loudnorm`
        // hält die Spitze selbst unter −1 dBTP, und was danach noch anschlägt,
        // fängt der Begrenzer wie bisher ab. Umgekehrt zöge die Normalisierung
        // hinter dem Begrenzer alles wieder über dessen Grenze.
        //
        // `loudnorm` gibt in 192 kHz aus und bringt einen Vorlauf mit; das
        // `aformat` und das `atrim` darunter setzen beides wieder gerade --
        // dieselben zwei Zeilen, die ohnehin schon dastanden.
        ...normalisierenFilter(timeline.settings.zielLautheit),
        'alimiter=limit=0.95:level=disabled',
        `apad=whole_dur=${fmt(total)}`,
        `atrim=duration=${fmt(total)}`,
        `aformat=sample_fmts=fltp:sample_rates=${ctx.sr}:channel_layouts=stereo`,
    ], 'a');
}
// ---------------------------------------------------------------------------
// entry point
// ---------------------------------------------------------------------------
/**
 * Die Bildgrösse, in der gerechnet wird -- gerade Zahlen, und für eine
 * Vorschau nicht grösser als nötig (7.80).
 *
 * Kleiner wird nur, was grösser als die Grenze ist: ein Projekt, das ohnehin
 * in 540 Zeilen ausgegeben wird, bleibt bei 540. Hochgerechnet wird nie --
 * das war ja gerade der Fehler.
 */
export function vorschauMass(width, height, grenze) {
    const gerade = (n) => Math.max(2, Math.round(n) - (Math.round(n) % 2));
    if (!grenze || !(grenze > 0) || !(height > 0) || height <= grenze) {
        return { W: gerade(width), H: gerade(height) };
    }
    const faktor = grenze / height;
    return { W: gerade(width * faktor), H: gerade(height * faktor) };
}
export function compileTimeline(o) {
    const timeline = Timeline.parse(o.timeline);
    const settings = timeline.settings;
    const ctx = {
        o: { ...o, timeline },
        g: new GraphBuilder(),
        inputs: [],
        aux: [],
        vorlaeufe: [],
        warnings: [],
        // h.264 needs even dimensions.
        ...vorschauMass(settings.width, settings.height, o.quality === 'preview' ? o.vorschauHoehe : undefined),
        fps: settings.fps,
        sr: settings.sampleRate,
        fileInputs: new Map(),
    };
    const total = Math.max(timelineDuration(timeline), 0.04);
    const videoTracks = timeline.tracks.filter((t) => t.kind === 'video');
    const base = buildBaseTrack(ctx, videoTracks[0], total);
    let video = buildOverlays(ctx, base.label, videoTracks.slice(1));
    if (timeline.subtitles.enabled && o.subtitlePath) {
        const opts = [`filename=${safePath(o.subtitlePath)}`];
        if (o.fontsDir)
            opts.push(`fontsdir=${safePath(o.fontsDir)}`);
        video = ctx.g.pipe(video, ['format=yuv420p', `subtitles=${opts.join(':')}`]);
    }
    // Für einen Film wird der Strom auf Bildrate und Länge gebracht. Für ein
    // einzelnes Bild (S22) nicht, und das ist keine Sparsamkeit: `fps` hält ein
    // Bild zurück, bis es weiss, wie lang es steht -- bei einem Strom aus genau
    // einem Bild kommt dann gar keines heraus („Output file is empty“, an einer
    // Ebene nachgestellt). Was `trim` begrenzen soll, begrenzt hier `-frames:v 1`.
    video = o.bild
        ? ctx.g.pipe(video, ['format=yuv420p'])
        : ctx.g.pipe(video, ['format=yuv420p', `fps=${fmt(ctx.fps)}`, `trim=duration=${fmt(total)}`, 'setpts=PTS-STARTPTS']);
    const vout = ctx.g.label('out');
    ctx.g.chain([video], ['null'], [vout]);
    // Ein Einzelbild (S22) bekommt gar keine Tonkette. Sie erst zu bauen und
    // dann nicht zu benutzen geht nicht: ffmpeg weist einen Graphen mit einem
    // offenen Ausgang ab -- „Filter afade:default has an unconnected output“.
    // Und sie zu bauen, nur um sie nach `null` zu schicken, hiesse jede
    // Tondatei zu öffnen und zu dekodieren, für ein Bild.
    let aout = null;
    if (!o.bild) {
        const audio = buildAudio(ctx, timeline, total);
        aout = ctx.g.label('out');
        // 7.71e: Die Naht eines Abschnitts. Zwei eigens kodierte Dateien treffen
        // mitten in einer Schwingung aufeinander, und AAC braucht am Dateianfang
        // einen Vorlauf -- beides knackt. Zehn Millisekunden herauf und herunter
        // nehmen das weg, ohne dass eine Lücke zu hören ist. Bei einem sehr
        // kurzen Abschnitt wird die Blende gekürzt, damit sie nicht die Hälfte
        // des Stücks frisst.
        const naht = o.nahtBlende && o.nahtBlende > 0 ? Math.min(o.nahtBlende, total / 4) : 0;
        ctx.g.chain([audio], naht > 0
            ? [`afade=t=in:st=0:d=${fmt(naht)}`, `afade=t=out:st=${fmt(Math.max(0, total - naht))}:d=${fmt(naht)}`]
            : ['anull'], [aout]);
    }
    const quality = o.quality ?? 'final';
    const args = ['-hide_banner', '-nostdin', '-y'];
    if (o.progress)
        args.push('-progress', 'pipe:1', '-nostats');
    args.push(...ctx.inputs);
    args.push('-filter_complex', ctx.g.build());
    args.push('-map', `[${vout}]`);
    if (aout)
        args.push('-map', `[${aout}]`);
    if (o.bild) {
        // Ein Bild, das erste. `-update 1` ist nicht nötig, `-frames:v 1` sagt
        // alles -- und die Endung im Ausgabenamen wählt den Codec.
        args.push('-frames:v', '1');
        if (o.bild === 'jpg')
            args.push('-q:v', '2');
        args.push(o.output);
        return {
            args,
            filterComplex: ctx.g.build(),
            auxFiles: ctx.aux,
            vorlaeufe: ctx.vorlaeufe,
            durationSec: total,
            warnings: ctx.warnings,
        };
    }
    if (o.range) {
        args.push('-ss', fmt(o.range.start), '-t', fmt(Math.max(0.04, o.range.end - o.range.start)));
    }
    else {
        args.push('-t', fmt(total));
    }
    const stufe = quality === 'preview' ? VORSCHAU_STUFE : EXPORT_STUFEN[o.guete ?? 'standard'];
    args.push('-c:v', 'libx264', '-preset', stufe.preset, '-crf', String(stufe.crf), '-pix_fmt', 'yuv420p', '-profile:v', 'high', '-level', h264Level(ctx.W, ctx.H, ctx.fps), '-r', fmt(ctx.fps), '-c:a', 'aac', '-b:a', stufe.ton, '-ar', String(ctx.sr), '-ac', '2', '-movflags', '+faststart', o.output);
    return {
        args,
        filterComplex: ctx.g.build(),
        auxFiles: ctx.aux,
        vorlaeufe: ctx.vorlaeufe,
        durationSec: o.range ? Math.max(0.04, o.range.end - o.range.start) : total,
        warnings: ctx.warnings,
    };
}
export { TRANSITIONS };
//# sourceMappingURL=compile.js.map